import React, { useState, useEffect } from 'react';
import { authService } from '../services/authService';

// Theme constants matching Plume Dashboard - matte colors
const THEME = {
    background: '#0a0a0f',
    cardBg: '#0c0c12',
    cardBgHover: '#151515',
    borderColor: '#1a1a24',
    borderHover: '#2a2a34',
    accent: '#5a6f8e',
    textPrimary: '#e8e8e8',
    textSecondary: '#888',
    textMuted: '#666',
    success: '#4a9d6b',
    warning: '#c9a227',
    danger: '#8b3a3a',
    dangerBright: '#a04545',
    fontFamily: 'Satoshi, -apple-system, sans-serif'
};

const AdminPanel = () => {
    const [activeTab, setActiveTab] = useState('users');

    const tabStyle = (isActive) => ({
        backgroundColor: isActive ? THEME.accent : 'transparent',
        border: isActive ? 'none' : `1px solid ${THEME.borderColor}`,
        color: THEME.textPrimary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.85rem',
        textTransform: 'lowercase',
        padding: '0.5rem 1rem',
        borderRadius: '6px',
        transition: 'all 0.2s ease'
    });

    return (
        <div className="container-fluid px-0" style={{ backgroundColor: THEME.background, minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="px-4 pt-4">
                <h4 className="mb-1" style={{
                    fontWeight: 700,
                    fontSize: '1.5rem',
                    letterSpacing: '-0.02em',
                    textTransform: 'lowercase',
                    color: THEME.textPrimary,
                    fontFamily: THEME.fontFamily
                }}>
                    user accounts
                </h4>
                <p className="mb-4" style={{
                    fontSize: '0.85rem',
                    textTransform: 'lowercase',
                    color: THEME.textSecondary,
                    fontFamily: THEME.fontFamily
                }}>
                    manage user accounts, billing status, and permissions
                </p>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 mb-4">
                <div className="d-flex gap-2">
                    <button
                        onClick={() => setActiveTab('users')}
                        style={tabStyle(activeTab === 'users')}
                    >
                        <i className="bi bi-people me-2"></i>
                        user directory
                    </button>
                    <button
                        onClick={() => setActiveTab('invite')}
                        style={tabStyle(activeTab === 'invite')}
                    >
                        <i className="bi bi-person-plus me-2"></i>
                        invite user
                    </button>
                    <button
                        onClick={() => setActiveTab('pending')}
                        style={tabStyle(activeTab === 'pending')}
                    >
                        <i className="bi bi-envelope-open me-2"></i>
                        pending invitations
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            <div className="px-4 pb-4">
                {activeTab === 'users' && <UserDirectory />}
                {activeTab === 'invite' && <InviteUserForm />}
                {activeTab === 'pending' && <PendingInvitations />}
            </div>
        </div>
    );
};

// User Directory Component with Expandable Rows
const UserDirectory = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [expandedUserId, setExpandedUserId] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await authService.adminGetUsers();
            if (result.ok) {
                setUsers(result.data.users || []);
            } else {
                setError('Failed to fetch users');
            }
        } catch (err) {
            setError('An error occurred while fetching users');
        } finally {
            setLoading(false);
        }
    };

    const updateUserInList = (updatedUser) => {
        setUsers(prev => prev.map(u => u.id === updatedUser.id ? { ...u, ...updatedUser } : u));
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (user.org && user.org.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (user.display_name && user.display_name.toLowerCase().includes(searchTerm.toLowerCase()));

        if (filterType === 'all') return matchesSearch;
        if (filterType === 'admin') return matchesSearch && user.is_admin;
        if (filterType === 'customer') return matchesSearch && !user.is_admin && user.contracts?.length > 0;
        if (filterType === 'active') return matchesSearch && user.has_active_session;
        if (filterType === 'verified') return matchesSearch && user.verified;
        return matchesSearch;
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return 'n/a';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).toLowerCase();
    };

    const getTimeSince = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'today';
        if (diffDays === 1) return 'yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
        return `${Math.floor(diffDays / 365)} years ago`;
    };

    const handleRowClick = (userId) => {
        setExpandedUserId(expandedUserId === userId ? null : userId);
    };

    const cardStyle = {
        backgroundColor: THEME.cardBg,
        border: `1px solid ${THEME.borderColor}`,
        borderRadius: '8px'
    };

    const inputStyle = {
        backgroundColor: THEME.cardBg,
        border: `1px solid ${THEME.borderColor}`,
        color: THEME.textPrimary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.85rem'
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm" style={{ color: THEME.accent }} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3" style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>loading users...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header with stats */}
            <div className="row g-3 mb-4">
                <div className="col-md-3">
                    <div style={cardStyle}>
                        <div className="text-center py-3">
                            <h3 style={{ color: THEME.accent, fontFamily: THEME.fontFamily, fontWeight: 700, marginBottom: '0.25rem' }}>{users.length}</h3>
                            <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>total users</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div style={cardStyle}>
                        <div className="text-center py-3">
                            <h3 style={{ color: THEME.success, fontFamily: THEME.fontFamily, fontWeight: 700, marginBottom: '0.25rem' }}>{users.filter(u => u.has_active_session).length}</h3>
                            <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>active sessions</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div style={cardStyle}>
                        <div className="text-center py-3">
                            <h3 style={{ color: THEME.accent, fontFamily: THEME.fontFamily, fontWeight: 700, marginBottom: '0.25rem' }}>{users.filter(u => u.contracts?.length > 0).length}</h3>
                            <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>customers</small>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div style={cardStyle}>
                        <div className="text-center py-3">
                            <h3 style={{ color: THEME.warning, fontFamily: THEME.fontFamily, fontWeight: 700, marginBottom: '0.25rem' }}>{users.filter(u => u.is_admin).length}</h3>
                            <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>admins</small>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filter */}
            <div style={cardStyle} className="mb-4">
                <div className="p-3">
                    <div className="row g-3 align-items-center">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text" style={{ ...inputStyle, borderRight: 'none' }}>
                                    <i className="bi bi-search" style={{ color: THEME.textSecondary }}></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="search by email, name, or organization..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ ...inputStyle, borderLeft: 'none', textTransform: 'lowercase' }}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                                style={{ ...inputStyle, textTransform: 'lowercase' }}
                            >
                                <option value="all">all users</option>
                                <option value="admin">admins only</option>
                                <option value="customer">customers only</option>
                                <option value="active">active sessions</option>
                                <option value="verified">verified only</option>
                            </select>
                        </div>
                        <div className="col-md-3 text-end">
                            <button
                                className="btn"
                                onClick={fetchUsers}
                                style={{
                                    backgroundColor: THEME.accent,
                                    color: 'white',
                                    border: 'none',
                                    fontFamily: THEME.fontFamily,
                                    fontSize: '0.85rem',
                                    textTransform: 'lowercase',
                                    padding: '0.5rem 1rem'
                                }}
                            >
                                <i className="bi bi-arrow-clockwise me-2"></i>
                                refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{
                    backgroundColor: 'rgba(139, 58, 58, 0.1)',
                    border: `1px solid ${THEME.danger}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <i className="bi bi-exclamation-triangle me-2" style={{ color: THEME.dangerBright }}></i>
                    <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily }}>{error}</span>
                </div>
            )}

            {/* Instruction Banner */}
            <div style={{
                backgroundColor: 'rgba(90, 111, 142, 0.1)',
                border: `1px solid ${THEME.accent}`,
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem'
            }}>
                <i className="bi bi-info-circle me-2" style={{ color: THEME.accent }}></i>
                <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                    click on any user row to expand and view/edit their account information
                </span>
            </div>

            {/* Users List */}
            <div style={cardStyle}>
                {filteredUsers.length === 0 ? (
                    <div className="text-center py-5" style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>
                        {searchTerm || filterType !== 'all'
                            ? 'no users match your filters'
                            : 'no users found'}
                    </div>
                ) : (
                    <div>
                        {filteredUsers.map((user, index) => (
                            <UserRow
                                key={user.id}
                                user={user}
                                isExpanded={expandedUserId === user.id}
                                onToggle={() => handleRowClick(user.id)}
                                formatDate={formatDate}
                                getTimeSince={getTimeSince}
                                onUserUpdate={updateUserInList}
                                isLast={index === filteredUsers.length - 1}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// Individual User Row Component
const UserRow = ({ user, isExpanded, onToggle, formatDate, getTimeSince, onUserUpdate, isLast }) => {
    const avatarBg = user.is_admin ? THEME.danger : THEME.accent;

    return (
        <div style={{
            borderBottom: isLast ? 'none' : `1px solid ${THEME.borderColor}`,
            transition: 'background-color 0.2s ease'
        }}>
            {/* Clickable Header Row */}
            <div
                className="d-flex align-items-center p-3"
                style={{
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? THEME.cardBgHover : 'transparent'
                }}
                onClick={onToggle}
            >
                {/* Expand/Collapse Icon */}
                <div className="me-3">
                    <i
                        className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                        style={{ color: THEME.accent, fontSize: '0.85rem' }}
                    ></i>
                </div>

                {/* User Avatar */}
                <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{
                        width: '40px',
                        height: '40px',
                        flexShrink: 0,
                        borderRadius: '50%',
                        backgroundColor: avatarBg,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: 'white',
                        fontFamily: THEME.fontFamily,
                        textTransform: 'uppercase'
                    }}
                >
                    {user.display_name ? user.display_name.charAt(0) : user.email.charAt(0)}
                </div>

                {/* User Info */}
                <div className="flex-grow-1">
                    <div className="d-flex align-items-center gap-2">
                        <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontWeight: 500, fontSize: '0.9rem' }}>
                            {user.display_name || user.email}
                        </span>
                        {user.has_active_session && (
                            <span style={{
                                backgroundColor: 'rgba(74, 157, 107, 0.2)',
                                border: `1px solid ${THEME.success}`,
                                color: THEME.success,
                                fontSize: '0.65rem',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontFamily: THEME.fontFamily,
                                textTransform: 'lowercase'
                            }}>
                                online
                            </span>
                        )}
                    </div>
                    <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, fontSize: '0.8rem', textTransform: 'lowercase' }}>
                        {user.display_name ? user.email : (user.is_admin ? 'administrator' : `tier: ${user.tier || 'free'}`)}
                        {user.org && ` • ${user.org}`}
                    </small>
                </div>

                {/* Status Badges */}
                <div className="d-flex gap-2 me-3">
                    {user.verified ? (
                        <span style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${THEME.success}`,
                            color: THEME.success,
                            fontSize: '0.7rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontFamily: THEME.fontFamily,
                            textTransform: 'lowercase'
                        }}>verified</span>
                    ) : (
                        <span style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${THEME.warning}`,
                            color: THEME.warning,
                            fontSize: '0.7rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontFamily: THEME.fontFamily,
                            textTransform: 'lowercase'
                        }}>pending</span>
                    )}
                    {user.is_admin && (
                        <span style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${THEME.dangerBright}`,
                            color: THEME.dangerBright,
                            fontSize: '0.7rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontFamily: THEME.fontFamily,
                            textTransform: 'lowercase'
                        }}>admin</span>
                    )}
                    {user.contracts && user.contracts.length > 0 && (
                        <span style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${THEME.accent}`,
                            color: THEME.accent,
                            fontSize: '0.7rem',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontFamily: THEME.fontFamily,
                            textTransform: 'lowercase'
                        }}>{user.contracts.length} contract{user.contracts.length > 1 ? 's' : ''}</span>
                    )}
                </div>

                {/* Created Date */}
                <div className="text-end" style={{ minWidth: '120px' }}>
                    <div style={{ color: THEME.textPrimary, fontSize: '0.8rem', fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>{getTimeSince(user.created_at)}</div>
                    <small style={{ color: THEME.textSecondary, fontSize: '0.7rem', fontFamily: THEME.fontFamily }}>{formatDate(user.created_at)}</small>
                </div>
            </div>

            {/* Expanded Content - Account Info Editor */}
            {isExpanded && (
                <ExpandedUserAccountInfo user={user} onUserUpdate={onUserUpdate} />
            )}
        </div>
    );
};

// Expanded User Account Info Component - For viewing and editing user account details
const ExpandedUserAccountInfo = ({ user, onUserUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null);

    // Editable fields
    const [displayName, setDisplayName] = useState(user.display_name || '');
    const [email, setEmail] = useState(user.email || '');
    const [org, setOrg] = useState(user.org || '');
    const [tier, setTier] = useState(user.tier || 'free');
    const [isAdmin, setIsAdmin] = useState(user.is_admin || false);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'n/a';
        return new Date(dateStr).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).toLowerCase();
    };

    const handleSave = async () => {
        setSaving(true);
        setSaveMessage(null);
        try {
            const result = await authService.adminUpdateUser(user.id, {
                display_name: displayName,
                email: email,
                org: org,
                tier: tier,
                is_admin: isAdmin
            });
            if (result.ok) {
                setSaveMessage({ type: 'success', text: 'account updated successfully' });
                setIsEditing(false);
                // Update parent state
                if (onUserUpdate) {
                    onUserUpdate({
                        id: user.id,
                        display_name: displayName,
                        email: email,
                        org: org,
                        tier: tier,
                        is_admin: isAdmin
                    });
                }
            } else {
                // Provide more specific error messages
                if (result.status === 404) {
                    setSaveMessage({ type: 'error', text: 'api endpoint not available - backend update required' });
                } else if (result.status === 403) {
                    setSaveMessage({ type: 'error', text: 'admin access required' });
                } else {
                    setSaveMessage({ type: 'error', text: result.error || 'failed to update account' });
                }
            }
        } catch (err) {
            setSaveMessage({ type: 'error', text: 'network error - please try again' });
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to original values
        setDisplayName(user.display_name || '');
        setEmail(user.email || '');
        setOrg(user.org || '');
        setTier(user.tier || 'free');
        setIsAdmin(user.is_admin || false);
        setIsEditing(false);
        setSaveMessage(null);
    };

    const fieldStyle = {
        backgroundColor: THEME.cardBg,
        border: `1px solid ${THEME.borderColor}`,
        borderRadius: '6px',
        padding: '1rem',
        marginBottom: '1rem'
    };

    const inputStyle = {
        backgroundColor: THEME.background,
        border: `1px solid ${THEME.borderHover}`,
        color: THEME.textPrimary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.85rem',
        padding: '0.5rem 0.75rem',
        borderRadius: '4px',
        width: '100%'
    };

    const labelStyle = {
        color: THEME.textSecondary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.75rem',
        textTransform: 'lowercase',
        marginBottom: '0.5rem',
        display: 'block'
    };

    const valueStyle = {
        color: THEME.textPrimary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.9rem'
    };

    return (
        <div style={{
            backgroundColor: THEME.background,
            borderTop: `1px solid ${THEME.borderColor}`,
            padding: '1.5rem'
        }}>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 style={{
                        color: THEME.textPrimary,
                        fontFamily: THEME.fontFamily,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        marginBottom: '0.25rem',
                        textTransform: 'lowercase'
                    }}>
                        <i className="bi bi-person-gear me-2" style={{ color: THEME.accent }}></i>
                        account information
                    </h5>
                    <p style={{
                        color: THEME.textSecondary,
                        fontFamily: THEME.fontFamily,
                        fontSize: '0.8rem',
                        marginBottom: 0,
                        textTransform: 'lowercase'
                    }}>
                        view and edit user account details
                    </p>
                </div>
                <div className="d-flex gap-2">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            style={{
                                backgroundColor: 'transparent',
                                border: `1px solid ${THEME.accent}`,
                                color: THEME.accent,
                                fontFamily: THEME.fontFamily,
                                fontSize: '0.8rem',
                                padding: '0.4rem 1rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                textTransform: 'lowercase'
                            }}
                        >
                            <i className="bi bi-pencil me-2"></i>
                            edit
                        </button>
                    ) : (
                        <>
                            <button
                                onClick={handleCancel}
                                style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${THEME.borderColor}`,
                                    color: THEME.textSecondary,
                                    fontFamily: THEME.fontFamily,
                                    fontSize: '0.8rem',
                                    padding: '0.4rem 1rem',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    textTransform: 'lowercase'
                                }}
                            >
                                cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                style={{
                                    backgroundColor: THEME.accent,
                                    border: 'none',
                                    color: 'white',
                                    fontFamily: THEME.fontFamily,
                                    fontSize: '0.8rem',
                                    padding: '0.4rem 1rem',
                                    borderRadius: '4px',
                                    cursor: saving ? 'not-allowed' : 'pointer',
                                    opacity: saving ? 0.6 : 1,
                                    textTransform: 'lowercase'
                                }}
                            >
                                {saving ? 'saving...' : 'save changes'}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Save Message */}
            {saveMessage && (
                <div style={{
                    backgroundColor: saveMessage.type === 'success' ? 'rgba(74, 157, 107, 0.1)' : 'rgba(139, 58, 58, 0.1)',
                    border: `1px solid ${saveMessage.type === 'success' ? THEME.success : THEME.danger}`,
                    borderRadius: '6px',
                    padding: '0.75rem 1rem',
                    marginBottom: '1rem'
                }}>
                    <i className={`bi ${saveMessage.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'} me-2`}
                        style={{ color: saveMessage.type === 'success' ? THEME.success : THEME.dangerBright }}></i>
                    <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                        {saveMessage.text}
                    </span>
                </div>
            )}

            {/* Account Fields */}
            <div className="row g-3">
                {/* Display Name */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>display name</label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="enter display name..."
                                style={inputStyle}
                            />
                        ) : (
                            <div style={valueStyle}>{displayName || <span style={{ color: THEME.textMuted }}>not set</span>}</div>
                        )}
                    </div>
                </div>

                {/* Email Address */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>email address</label>
                        {isEditing ? (
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="enter email..."
                                style={inputStyle}
                            />
                        ) : (
                            <div style={valueStyle}>{email}</div>
                        )}
                    </div>
                </div>

                {/* Organization */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>organization</label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={org}
                                onChange={(e) => setOrg(e.target.value)}
                                placeholder="enter organization..."
                                style={inputStyle}
                            />
                        ) : (
                            <div style={valueStyle}>{org || <span style={{ color: THEME.textMuted }}>not set</span>}</div>
                        )}
                    </div>
                </div>

                {/* Tier */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>subscription tier</label>
                        {isEditing ? (
                            <select
                                value={tier}
                                onChange={(e) => setTier(e.target.value)}
                                style={{ ...inputStyle, cursor: 'pointer' }}
                            >
                                <option value="free">free</option>
                                <option value="basic">basic</option>
                                <option value="enterprise">enterprise</option>
                            </select>
                        ) : (
                            <div>
                                <span style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${tier === 'enterprise' ? THEME.warning : tier === 'basic' ? THEME.accent : THEME.textMuted}`,
                                    color: tier === 'enterprise' ? THEME.warning : tier === 'basic' ? THEME.accent : THEME.textSecondary,
                                    fontSize: '0.75rem',
                                    padding: '3px 10px',
                                    borderRadius: '4px',
                                    fontFamily: THEME.fontFamily,
                                    textTransform: 'lowercase'
                                }}>{tier}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Admin Toggle */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>admin privileges</label>
                        {isEditing ? (
                            <div className="d-flex align-items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={isAdmin}
                                    onChange={(e) => setIsAdmin(e.target.checked)}
                                    style={{
                                        width: '18px',
                                        height: '18px',
                                        cursor: 'pointer',
                                        accentColor: THEME.accent
                                    }}
                                />
                                <span style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                                    grant admin access
                                </span>
                            </div>
                        ) : (
                            <div>
                                {isAdmin ? (
                                    <span style={{
                                        backgroundColor: 'transparent',
                                        border: `1px solid ${THEME.dangerBright}`,
                                        color: THEME.dangerBright,
                                        fontSize: '0.75rem',
                                        padding: '3px 10px',
                                        borderRadius: '4px',
                                        fontFamily: THEME.fontFamily,
                                        textTransform: 'lowercase'
                                    }}>admin</span>
                                ) : (
                                    <span style={{ color: THEME.textMuted, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                                        standard user
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Verification Status */}
                <div className="col-md-6">
                    <div style={fieldStyle}>
                        <label style={labelStyle}>verification status</label>
                        <div>
                            {user.verified ? (
                                <span style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${THEME.success}`,
                                    color: THEME.success,
                                    fontSize: '0.75rem',
                                    padding: '3px 10px',
                                    borderRadius: '4px',
                                    fontFamily: THEME.fontFamily,
                                    textTransform: 'lowercase'
                                }}>
                                    <i className="bi bi-check-circle me-1"></i>
                                    verified
                                </span>
                            ) : (
                                <span style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${THEME.warning}`,
                                    color: THEME.warning,
                                    fontSize: '0.75rem',
                                    padding: '3px 10px',
                                    borderRadius: '4px',
                                    fontFamily: THEME.fontFamily,
                                    textTransform: 'lowercase'
                                }}>
                                    <i className="bi bi-clock me-1"></i>
                                    pending verification
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Account Metadata */}
            <div style={{
                backgroundColor: THEME.cardBg,
                border: `1px solid ${THEME.borderColor}`,
                borderRadius: '6px',
                padding: '1rem',
                marginTop: '0.5rem'
            }}>
                <h6 style={{
                    color: THEME.textSecondary,
                    fontFamily: THEME.fontFamily,
                    fontSize: '0.75rem',
                    textTransform: 'lowercase',
                    marginBottom: '1rem'
                }}>account metadata</h6>
                <div className="row g-3">
                    <div className="col-md-4">
                        <div style={{ ...labelStyle, marginBottom: '0.25rem' }}>user id</div>
                        <code style={{ color: THEME.accent, fontSize: '0.75rem', fontFamily: 'monospace' }}>{user.id}</code>
                    </div>
                    <div className="col-md-4">
                        <div style={{ ...labelStyle, marginBottom: '0.25rem' }}>created at</div>
                        <div style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem' }}>
                            {formatDate(user.created_at)}
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div style={{ ...labelStyle, marginBottom: '0.25rem' }}>last login</div>
                        <div style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem' }}>
                            {formatDate(user.last_login) || <span style={{ color: THEME.textMuted }}>never</span>}
                        </div>
                    </div>
                </div>

                {/* Contracts Section */}
                {user.contracts && user.contracts.length > 0 && (
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${THEME.borderColor}` }}>
                        <div style={{ ...labelStyle, marginBottom: '0.5rem' }}>assigned contracts ({user.contracts.length})</div>
                        <div className="d-flex flex-wrap gap-2">
                            {user.contracts.map((contract, idx) => (
                                <code
                                    key={idx}
                                    style={{
                                        backgroundColor: THEME.background,
                                        border: `1px solid ${THEME.borderColor}`,
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        color: THEME.accent,
                                        fontSize: '0.7rem'
                                    }}
                                >
                                    {typeof contract === 'object' ? contract.contract_address : contract}
                                </code>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Invite User Form Component
const InviteUserForm = () => {
    const [email, setEmail] = useState('');
    const [org, setOrg] = useState('');
    const [contractAddress, setContractAddress] = useState('');
    const [role, setRole] = useState('viewer');
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setMessage('');

        setLoading(true);

        try {
            const result = await authService.adminInviteUser(email, org, contractAddress, role, notes);

            if (result.ok) {
                setMessage(`invitation sent to ${email}! they will receive an email with a secure link to create their account.`);
                setEmail('');
                setOrg('');
                setContractAddress('');
                setRole('viewer');
                setNotes('');
            } else if (result.status === 409) {
                setError('email already exists');
            } else if (result.status === 403) {
                setError('admin access required');
            } else {
                setError('failed to send invitation. please try again.');
            }
        } catch (err) {
            setError('an error occurred. please try again.');
        } finally {
            setLoading(false);
        }
    };

    const cardStyle = {
        backgroundColor: THEME.cardBg,
        border: `1px solid ${THEME.borderColor}`,
        borderRadius: '8px',
        padding: '1.5rem'
    };

    const inputStyle = {
        backgroundColor: THEME.background,
        border: `1px solid ${THEME.borderHover}`,
        color: THEME.textPrimary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.85rem',
        padding: '0.6rem 0.75rem',
        borderRadius: '6px',
        width: '100%'
    };

    const labelStyle = {
        color: THEME.textSecondary,
        fontFamily: THEME.fontFamily,
        fontSize: '0.8rem',
        textTransform: 'lowercase',
        marginBottom: '0.5rem',
        display: 'block'
    };

    const helpTextStyle = {
        color: THEME.textMuted,
        fontFamily: THEME.fontFamily,
        fontSize: '0.75rem',
        marginTop: '0.25rem',
        textTransform: 'lowercase'
    };

    return (
        <div className="row">
            <div className="col-lg-6">
                <div style={cardStyle}>
                    <h4 style={{
                        color: THEME.textPrimary,
                        fontFamily: THEME.fontFamily,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        marginBottom: '1.5rem',
                        textTransform: 'lowercase'
                    }}>
                        <i className="bi bi-envelope-plus me-2" style={{ color: THEME.accent }}></i>
                        send invitation
                    </h4>

                    {message && (
                        <div style={{
                            backgroundColor: 'rgba(74, 157, 107, 0.1)',
                            border: `1px solid ${THEME.success}`,
                            borderRadius: '6px',
                            padding: '0.75rem 1rem',
                            marginBottom: '1rem'
                        }}>
                            <i className="bi bi-check-circle me-2" style={{ color: THEME.success }}></i>
                            <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                                {message}
                            </span>
                        </div>
                    )}

                    {error && (
                        <div style={{
                            backgroundColor: 'rgba(139, 58, 58, 0.1)',
                            border: `1px solid ${THEME.danger}`,
                            borderRadius: '6px',
                            padding: '0.75rem 1rem',
                            marginBottom: '1rem'
                        }}>
                            <i className="bi bi-exclamation-triangle me-2" style={{ color: THEME.dangerBright }}></i>
                            <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.85rem', textTransform: 'lowercase' }}>
                                {error}
                            </span>
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label style={labelStyle}>
                                email address <span style={{ color: THEME.dangerBright }}>*</span>
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                placeholder="user@example.com"
                                style={inputStyle}
                            />
                        </div>

                        <div className="mb-3">
                            <label style={labelStyle}>organization</label>
                            <input
                                type="text"
                                value={org}
                                onChange={(e) => setOrg(e.target.value)}
                                disabled={loading}
                                placeholder="e.g. circle"
                                style={inputStyle}
                            />
                            <div style={helpTextStyle}>organization name for this user</div>
                        </div>

                        <div className="mb-3">
                            <label style={labelStyle}>
                                smart contract address <span style={{ color: THEME.dangerBright }}>*</span>
                            </label>
                            <input
                                type="text"
                                value={contractAddress}
                                onChange={(e) => setContractAddress(e.target.value)}
                                required
                                disabled={loading}
                                placeholder="0x..."
                                style={inputStyle}
                            />
                            <div style={helpTextStyle}>the contract address this customer will monitor for sybil activity</div>
                        </div>

                        <div className="mb-3">
                            <label style={labelStyle}>role</label>
                            <select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                disabled={loading}
                                style={{ ...inputStyle, cursor: 'pointer' }}
                            >
                                <option value="viewer">viewer</option>
                                <option value="admin">admin</option>
                            </select>
                            <div style={helpTextStyle}>access level for this user within their organization</div>
                        </div>

                        <div className="mb-4">
                            <label style={labelStyle}>notes (optional)</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                disabled={loading}
                                rows={3}
                                placeholder="internal notes about this user..."
                                style={{ ...inputStyle, resize: 'vertical' }}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                backgroundColor: THEME.accent,
                                border: 'none',
                                color: 'white',
                                fontFamily: THEME.fontFamily,
                                fontSize: '0.85rem',
                                padding: '0.6rem 1.25rem',
                                borderRadius: '6px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.6 : 1,
                                textTransform: 'lowercase'
                            }}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    sending invitation...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-envelope-plus me-2"></i>
                                    send invitation
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <div style={{ ...cardStyle, marginTop: '1rem' }}>
                    <h5 style={{
                        color: THEME.textPrimary,
                        fontFamily: THEME.fontFamily,
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        marginBottom: '1rem',
                        textTransform: 'lowercase'
                    }}>
                        <i className="bi bi-info-circle me-2" style={{ color: THEME.accent }}></i>
                        how invitations work
                    </h5>
                    <ul style={{
                        color: THEME.textSecondary,
                        fontFamily: THEME.fontFamily,
                        fontSize: '0.85rem',
                        marginBottom: 0,
                        paddingLeft: '1.25rem',
                        textTransform: 'lowercase'
                    }}>
                        <li style={{ marginBottom: '0.5rem' }}>users will receive a secure invitation link via email</li>
                        <li style={{ marginBottom: '0.5rem' }}>they will create their own password when accepting the invite</li>
                        <li style={{ marginBottom: '0.5rem' }}>each customer must be assigned a contract address they will monitor</li>
                        <li style={{ marginBottom: '0.5rem' }}>customers will see a dashboard with sybil data specific to their contract</li>
                        <li style={{ marginBottom: '0.5rem' }}>you can specify their organization and role (admin or viewer)</li>
                        <li>use the notes field to keep track of internal information</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// Pending Invitations Component
const PendingInvitations = () => {
    const [invitations, setInvitations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchInvitations();
    }, []);

    const fetchInvitations = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await authService.adminGetInvitations();
            if (result.ok) {
                setInvitations(result.data.invitations || []);
            } else {
                setError('failed to fetch invitations');
            }
        } catch (err) {
            setError('an error occurred while fetching invitations');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'n/a';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).toLowerCase();
    };

    const getTimeRemaining = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diffMs = expires - now;

        if (diffMs <= 0) return 'expired';

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        if (hours < 24) return `${hours}h remaining`;

        const days = Math.floor(hours / 24);
        return `${days}d remaining`;
    };

    const cardStyle = {
        backgroundColor: THEME.cardBg,
        border: `1px solid ${THEME.borderColor}`,
        borderRadius: '8px'
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm" style={{ color: THEME.accent }} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3" style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>loading invitations...</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 style={{
                    color: THEME.textPrimary,
                    fontFamily: THEME.fontFamily,
                    fontWeight: 600,
                    fontSize: '1rem',
                    marginBottom: 0,
                    textTransform: 'lowercase'
                }}>
                    <i className="bi bi-envelope-open me-2" style={{ color: THEME.accent }}></i>
                    pending invitations ({invitations.length})
                </h5>
                <button
                    onClick={fetchInvitations}
                    style={{
                        backgroundColor: 'transparent',
                        border: `1px solid ${THEME.accent}`,
                        color: THEME.accent,
                        fontFamily: THEME.fontFamily,
                        fontSize: '0.8rem',
                        padding: '0.4rem 0.75rem',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        textTransform: 'lowercase'
                    }}
                >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    refresh
                </button>
            </div>

            {error && (
                <div style={{
                    backgroundColor: 'rgba(139, 58, 58, 0.1)',
                    border: `1px solid ${THEME.danger}`,
                    borderRadius: '8px',
                    padding: '1rem',
                    marginBottom: '1rem'
                }}>
                    <i className="bi bi-exclamation-triangle me-2" style={{ color: THEME.dangerBright }}></i>
                    <span style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily }}>{error}</span>
                </div>
            )}

            {invitations.length === 0 ? (
                <div style={cardStyle}>
                    <div className="text-center py-5">
                        <i className="bi bi-inbox" style={{ fontSize: '2.5rem', color: THEME.textMuted }}></i>
                        <p style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, marginTop: '1rem', marginBottom: '0.25rem', textTransform: 'lowercase' }}>no pending invitations</p>
                        <small style={{ color: THEME.textMuted, fontFamily: THEME.fontFamily, textTransform: 'lowercase' }}>invitations you send will appear here until they are accepted</small>
                    </div>
                </div>
            ) : (
                <div style={cardStyle}>
                    {invitations.map((inv, index) => (
                        <div
                            key={inv.id}
                            style={{
                                padding: '1rem 1.25rem',
                                borderBottom: index === invitations.length - 1 ? 'none' : `1px solid ${THEME.borderColor}`,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem'
                            }}
                        >
                            {/* Email */}
                            <div style={{ flex: 2 }}>
                                <div style={{ color: THEME.textPrimary, fontFamily: THEME.fontFamily, fontSize: '0.9rem' }}>{inv.email}</div>
                                {inv.org && (
                                    <div style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, fontSize: '0.75rem', textTransform: 'lowercase' }}>{inv.org}</div>
                                )}
                            </div>

                            {/* Contract */}
                            <div style={{ flex: 2 }}>
                                {inv.contract_address ? (
                                    <code style={{
                                        color: THEME.accent,
                                        fontSize: '0.75rem',
                                        backgroundColor: THEME.background,
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        border: `1px solid ${THEME.borderColor}`
                                    }}>
                                        {inv.contract_address.substring(0, 10)}...{inv.contract_address.slice(-6)}
                                    </code>
                                ) : (
                                    <span style={{ color: THEME.textMuted }}>—</span>
                                )}
                            </div>

                            {/* Role */}
                            <div style={{ flex: 1 }}>
                                <span style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${inv.role === 'admin' ? THEME.dangerBright : THEME.textMuted}`,
                                    color: inv.role === 'admin' ? THEME.dangerBright : THEME.textSecondary,
                                    fontSize: '0.7rem',
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontFamily: THEME.fontFamily,
                                    textTransform: 'lowercase'
                                }}>
                                    {inv.role}
                                </span>
                            </div>

                            {/* Expires */}
                            <div style={{ flex: 1.5 }}>
                                <span style={{
                                    backgroundColor: 'transparent',
                                    border: `1px solid ${THEME.warning}`,
                                    color: THEME.warning,
                                    fontSize: '0.7rem',
                                    padding: '3px 8px',
                                    borderRadius: '4px',
                                    fontFamily: THEME.fontFamily,
                                    textTransform: 'lowercase'
                                }}>
                                    {getTimeRemaining(inv.expires_at)}
                                </span>
                                <div style={{ color: THEME.textMuted, fontFamily: THEME.fontFamily, fontSize: '0.7rem', marginTop: '0.25rem', textTransform: 'lowercase' }}>
                                    {formatDate(inv.expires_at)}
                                </div>
                            </div>

                            {/* Notes */}
                            <div style={{ flex: 2 }}>
                                {inv.notes ? (
                                    <small style={{ color: THEME.textSecondary, fontFamily: THEME.fontFamily, fontSize: '0.8rem' }}>{inv.notes}</small>
                                ) : (
                                    <span style={{ color: THEME.textMuted }}>—</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminPanel;
